import React, { useState } from "react";
import "./HigherLower.css";

const TOPICS = [
  { name: "Artificial intelligence", category: "Big ideas", value: 96 },
  { name: "Open source", category: "Community", value: 88 },
  { name: "Cybersecurity", category: "Stay sharp", value: 91 },
  { name: "Cloud computing", category: "The backbone", value: 82 },
  { name: "Virtual reality", category: "New worlds", value: 73 },
  { name: "Robotics", category: "Made to move", value: 79 },
  { name: "Web development", category: "On the web", value: 94 },
  { name: "Quantum computing", category: "What comes next", value: 68 },
  { name: "Mobile apps", category: "In your pocket", value: 86 },
  { name: "Data science", category: "Find the signal", value: 89 },
];

function shuffleTopics() {
  const topics = [...TOPICS];

  for (let index = topics.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [topics[index], topics[swapIndex]] = [topics[swapIndex], topics[index]];
  }

  return topics;
}

function HigherLower() {
  const [topics, setTopics] = useState(shuffleTopics);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [result, setResult] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [declined, setDeclined] = useState(false);

  const leftTopic = topics[round];
  const rightTopic = topics[round + 1];
  const isLastRound = round === topics.length - 2;

  function guessHigher(isHigher) {
    if (result) return;

    const isCorrect =
      isHigher === (rightTopic.value > leftTopic.value);

    setResult({ isCorrect });

    if (isCorrect) {
      setScore((currentScore) => currentScore + 1);
      const nextStreak = streak + 1;
      setStreak(nextStreak);
      setBestStreak((currentBest) => Math.max(currentBest, nextStreak));
    } else {
      setStreak(0);
    }
  }

  function nextRound() {
    if (isLastRound) {
      setTopics(shuffleTopics());
      setRound(0);
      setScore(0);
      setStreak(0);
    } else {
      setRound((currentRound) => currentRound + 1);
    }

    setResult(null);
  }

  return (
    <section className="higher-lower" aria-labelledby="higher-lower-title">
      <div className="higher-lower__inner">
        <div className="higher-lower__intro">
          <p className="higher-lower__eyebrow">A little tech intuition</p>
          <h2 id="higher-lower-title">Higher or lower?</h2>
          <p className="higher-lower__description">
            Which topic has the higher popularity score? Trust your gut and see
            how long your streak can last.
          </p>
        </div>

        <div className="higher-lower__game">
          {!gameStarted ? (
            <div className="higher-lower__welcome">
              <p className="higher-lower__welcome-label">
                {declined ? "No rush" : "Ready for a quick round?"}
              </p>
              <h3>
                {declined
                  ? "The game will be here when you are."
                  : "Put your tech intuition to the test."}
              </h3>
              <p>
                Nine matchups. One simple choice each time. See how many you
                can get right.
              </p>
              <div className="higher-lower__welcome-actions">
                <button
                  className="higher-lower__next"
                  type="button"
                  onClick={() => setGameStarted(true)}
                >
                  Play now
                </button>
                {!declined && (
                  <button
                    className="higher-lower__not-now"
                    type="button"
                    onClick={() => setDeclined(true)}
                  >
                    Not now
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
          <div className="higher-lower__scoreboard" aria-label="Game score">
            <p className="higher-lower__round">
              ROUND <strong>{round + 1}</strong> / {topics.length - 1}
            </p>
            <div className="higher-lower__stats">
              <span>Score <strong>{score}</strong></span>
              <span>Streak <strong>{streak}</strong></span>
              <span>Best <strong>{bestStreak}</strong></span>
            </div>
          </div>

          <div className="higher-lower__matchup">
            <article className="higher-lower__topic higher-lower__topic--left">
              <span className="higher-lower__topic-index">01</span>
              <span className="higher-lower__category">{leftTopic.category}</span>
              <h3>{leftTopic.name}</h3>
              <p className="higher-lower__metric-label">Popularity score</p>
              <p className="higher-lower__value">{leftTopic.value}</p>
            </article>

            <span className="higher-lower__versus" aria-hidden="true">
              VS
            </span>

            <article className="higher-lower__topic higher-lower__topic--right">
              <span className="higher-lower__topic-index">02</span>
              <span className="higher-lower__category">{rightTopic.category}</span>
              <h3>{rightTopic.name}</h3>
              <p className="higher-lower__metric-label">Popularity score</p>
              <p
                className={`higher-lower__value${result ? " is-revealed" : " is-hidden"}`}
                aria-label={result ? `Score ${rightTopic.value}` : "Score hidden"}
              >
                {result ? rightTopic.value : "?"}
              </p>
            </article>
          </div>

          {!result ? (
            <div className="higher-lower__choices" aria-label="Make your guess">
              <button type="button" onClick={() => guessHigher(false)}>
                Lower
              </button>
              <button type="button" onClick={() => guessHigher(true)}>
                Higher
              </button>
            </div>
          ) : (
            <div className="higher-lower__feedback" aria-live="polite">
              <p className={result.isCorrect ? "is-correct" : "is-incorrect"}>
                {result.isCorrect ? "Nice call." : "Not this time."} {rightTopic.name} is
                {rightTopic.value > leftTopic.value ? " higher" : " lower"}.
              </p>
              <button
                className="higher-lower__next"
                type="button"
                onClick={nextRound}
              >
                {isLastRound ? "Play again" : "Next matchup"}
              </button>
            </div>
          )}

          <p className="higher-lower__note">
            Just for fun: scores are illustrative game data, not live search
            statistics.
          </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export default HigherLower;